import Pino, { type LoggerOptions } from 'pino';

import type { LoggerStore, SourceAwareLoggerInterface } from '../types';

// Get log level from environment variable, default to appropriate level based on environment
const getLogLevel = (): Pino.Level => {
  const level = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
  const validLevels: Pino.Level[] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];

  if (level && validLevels.includes(level as Pino.Level)) {
    return level as Pino.Level;
  }

  // Default to 'info' in production, 'debug' in development
  return import.meta.env.PROD ? 'info' : 'debug';
};

// Store reference for logging state
let storeReference: LoggerStore | null = null;

// Track discovered sources locally for performance
const discoveredSourcesCache = new Set<string>();

// Default to disabled until store is connected and explicitly enabled
let isStoreConnected = false;

export const setLoggerStore = (store: LoggerStore) => {
  storeReference = store;
  isStoreConnected = true;
};

// Page/component specific log control
const getDisabledLogSources = (): string[] => {
  // Fallback to environment variable if store not available
  if (!storeReference) {
    const disabled = import.meta.env.VITE_DISABLED_LOG_SOURCES?.split(',') || [];
    return disabled.map((source: string) => source.trim().toLowerCase());
  }

  try {
    const state = storeReference.getState();
    return state.logging?.disabledSources || [];
  } catch {
    return [];
  }
};

const isGlobalLoggingDisabled = (): boolean => {
  // Check environment variable for early logging control
  const envDisabled = import.meta.env.VITE_DISABLE_ALL_LOGGING === 'true';
  if (envDisabled) {
    return true;
  }

  // If store not connected yet, check if we should allow early logs
  if (!isStoreConnected || !storeReference) {
    // Allow early logs only if explicitly enabled via env var
    return import.meta.env.VITE_ENABLE_EARLY_LOGGING !== 'true';
  }

  try {
    const state = storeReference.getState();
    // If logging state doesn't exist, default to disabled
    return state.logging?.isGloballyDisabled ?? true;
  } catch {
    return true; // On error, default to disabled
  }
};

// Enhanced logger with source filtering
export class SourceAwareLogger implements SourceAwareLoggerInterface {
  private baseLogger: Pino.Logger;

  constructor() {
    const logLevel = getLogLevel();

    const pinoConfig: LoggerOptions = {
      browser: {
        asObject: true,
      },
      level: logLevel,
      formatters: {
        level: (label: string) => ({ severity: label.toUpperCase() }),
      },
      timestamp: Pino.stdTimeFunctions.isoTime,
    };

    // Enable pretty printing in development or when log level is debug/trace
    if (import.meta.env.DEV || ['debug', 'trace'].includes(logLevel)) {
      pinoConfig.transport = {
        target: 'pino-pretty',
        options: {
          colorize: true,
          translateTime: 'SYS:standard',
          ignore: 'pid,hostname',
        },
      };
    }

    this.baseLogger = Pino(pinoConfig);
  }

  private isSourceDisabled(source?: string): boolean {
    // Check global disable first - if globally disabled, block everything
    if (isGlobalLoggingDisabled()) {
      return true;
    }

    if (!source) {
      return false;
    }

    const disabledSources = getDisabledLogSources();
    // Check if the source is in the disabled list (case-sensitive exact match)
    return disabledSources.includes(source);
  }

  private trackDiscoveredSource(source: string): void {
    // Add to local cache
    if (!discoveredSourcesCache.has(source)) {
      discoveredSourcesCache.add(source);

      // Add to Redux store if available
      if (storeReference?.dispatch) {
        try {
          // Import action dynamically to avoid circular dependency
          import('../store/loggingReducer')
            .then(({ addDiscoveredSource }) => {
              if (storeReference?.dispatch) {
                storeReference.dispatch(addDiscoveredSource(source));
              }
              return;
            })
            .catch(
              () =>
                // Silently ignore import/dispatch errors
                undefined,
            );
        } catch {
          // Silently fail if we can't update the store
        }
      }
    }
  }

  private extractSourceFromMessage(msg: string): string | undefined {
    // Extract source from messages like '[ComponentName]' or '[ComponentName] message'
    const match = msg.match(/^\[([^\]]+)\]/);
    return match ? match[1] : undefined;
  }

  private detectCallingSource(): string | undefined {
    try {
      // Create an error to get the stack trace
      const err = new Error();
      const stack = err.stack || '';
      const stackLines = stack.split('\n');

      // Skip the first few lines (Error message, this function, and the logger method)
      for (let i = 3; i < stackLines.length; i++) {
        const line = stackLines[i];

        // Skip internal logger calls and webpack runtime
        if (line.includes('Logger.ts') || line.includes('webpack') || line.includes('node_modules')) {
          continue;
        }

        // Try to extract filename from the stack trace
        // Match patterns like "at functionName (file.ts:123:45)" or "at file.ts:123:45"
        const fileMatch = line.match(/(?:at\s+)?(?:.*?\s+\()?([^/\s]+?\.(tsx?|jsx?))/);
        if (fileMatch) {
          // Remove file extension and return
          return fileMatch[1].replace(/\.(tsx?|jsx?)$/, '');
        }

        // Try to extract component/function name
        const funcMatch = line.match(/at\s+(\w+)/);
        if (funcMatch && funcMatch[1] !== 'Object' && funcMatch[1] !== 'Module') {
          return funcMatch[1];
        }
      }
    } catch {
      // Silently fail if we can't get stack trace
    }

    return undefined;
  }

  private formatMessage(msg: string, detectedSource?: string): string {
    // If message already has a source identifier, use it as-is
    if (msg.match(/^\[([^\]]+)\]/)) {
      return msg;
    }

    // If we detected a source, prepend it
    if (detectedSource) {
      return `[${detectedSource}] ${msg}`;
    }

    return msg;
  }

  trace(msg: string, ...args: unknown[]) {
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread; args are user-supplied log payloads
      this.baseLogger.trace(formattedMsg, ...(args as any[]));
    }
  }

  debug(msg: string, ...args: unknown[]) {
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread
      this.baseLogger.debug(formattedMsg, ...(args as any[]));
    }
  }

  info(msg: string, ...args: unknown[]) {
    // If globally disabled, block all logs (including those without a source)
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread
      this.baseLogger.info(formattedMsg, ...(args as any[]));
    }
  }

  warn(msg: string, ...args: unknown[]) {
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread
      this.baseLogger.warn(formattedMsg, ...(args as any[]));
    }
  }

  error(msg: string, ...args: unknown[]) {
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread
      this.baseLogger.error(formattedMsg, ...(args as any[]));
    }
  }

  fatal(msg: string, ...args: unknown[]) {
    if (isGlobalLoggingDisabled()) {
      return;
    }

    // Try to get source from message first, then auto-detect
    const source = this.extractSourceFromMessage(msg);
    const detectedSource = source || this.detectCallingSource();

    // Track the source if found
    if (detectedSource) {
      this.trackDiscoveredSource(detectedSource);
    }

    if (!this.isSourceDisabled(detectedSource)) {
      const formattedMsg = this.formatMessage(msg, detectedSource);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pino overloads reject unknown[] spread
      this.baseLogger.fatal(formattedMsg, ...(args as any[]));
    }
  }
}

// Create default logger instance
export const logger = new SourceAwareLogger();

// Export convenience function
export const log = (msg: string) => logger.info(msg);

export default logger;
